import { useRouter } from 'next/router';

import { Toast, useToast } from '@zillow/constellation-10';

import {
  EnterpriseListingEvent,
  RentalManagerEnterpriseEventTrackingWebApiHandler_trackCustomerListingEvent,
} from '@zg-rentals/ts-rental-manager-api-client';

import { useAuth } from '../components/AuthProvider';
import { useGetListingDetails } from './queries/properties';

import type { Client } from '@zg-rentals/http-client';
import type { UseMutationOptions, UseMutationResult } from '@zg-rentals/react-query5';

type UsePropertyEditMutationOptions<TData, TError, TVariables, TContext> = {
  mutationHook: (
    options?: Omit<UseMutationOptions<TData, TError, TVariables, TContext>, 'mutationFn'> & {
      httpClient?: Client;
    },
  ) => UseMutationResult<TData, TError, TVariables, TContext>;
  onSuccess?: (data: TData, variables: TVariables, context: TContext) => void;
  onError?: (error: TError, variables: TVariables, context: TContext) => void;
  successToastMessage?: string;
  skipToast?: boolean;
  skipTracking?: boolean;
  meta?: {
    invalidates?: Array<string>;
    context?: Record<string, unknown>;
  };
  httpClient?: Client;
  // Falls back to the route's listingAlias when omitted (e.g. when editing outside a listing page).
  listingAlias?: string;
};

/**
 * Wrapper hook for property edit mutations that handles:
 * 1. Success toast notification
 * 2. Enterprise event tracking (EDIT event)
 * 3. Automatic error recalculation (via React Query invalidation)
 * 4. Details and recommendations query invalidation
 * 5. Listing alias validation
 *
 * @example
 * ```typescript
 * const { mutateAsync: updateBuildingDetails } = usePropertyEditMutation({
 *   mutationHook: useRentalManagerUserPropertyWebApiHandler_updateBuildingDetails,
 *   successToastMessage: 'Building details updated',
 *   onError: (error) => {
 *     enqueueToast(<Toast tone="critical">Failed to update</Toast>);
 *   },
 *   meta: {
 *     // listingDetails is automatically invalidated - add any additional tags here
 *     invalidates: ['someOtherQuery'],
 *     context: { api: 'updateBuildingDetails', details: { listingAlias } },
 *   },
 * });
 * ```
 */
export const usePropertyEditMutation = <TData = unknown, TError = unknown, TVariables = unknown, TContext = unknown>({
  mutationHook,
  onSuccess,
  onError,
  successToastMessage = 'Changes saved',
  skipToast = false,
  skipTracking = false,
  meta,
  httpClient,
  listingAlias: listingAliasProp,
}: UsePropertyEditMutationOptions<TData, TError, TVariables, TContext>) => {
  const router = useRouter();
  const listingAlias = listingAliasProp ?? (router.query.listingAlias as string);
  const { user } = useAuth();
  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const { enqueueToast } = useToast();

  const mutationOptions: Record<string, unknown> = {
    onSuccess: (data: TData, variables: TVariables, context: TContext) => {
      if (listingDetails?.isMultifamily) {
        if (!skipToast) {
          enqueueToast(<Toast tone="success">{successToastMessage}</Toast>);
        }

        if (
          !skipTracking &&
          user?.customers?.length &&
          !user?.impersonatorEmail &&
          !user?.impersonatorUserToken &&
          listingDetails?.feedId &&
          listingDetails?.feedListingId
        ) {
          RentalManagerEnterpriseEventTrackingWebApiHandler_trackCustomerListingEvent({
            feedId: listingDetails.feedId,
            feedListingId: listingDetails.feedListingId,
            event: EnterpriseListingEvent.EDIT,
          }).catch(() => {
            // Silently fail - tracking should not block user actions
          });
        }
      }

      onSuccess?.(data, variables, context);
    },
    onError: (error: TError, variables: TVariables, context: TContext) => {
      onError?.(error, variables, context);
    },
    meta: {
      // Always invalidate listingDetails and listingRecommendations on property edits, plus any additional tags
      invalidates: ['listingDetails', 'listingRecommendations', ...(meta?.invalidates || [])],
      context: meta?.context,
    },
  };

  // Add httpClient if provided
  if (httpClient) {
    mutationOptions.httpClient = httpClient;
  }

  const mutationResult = mutationHook(mutationOptions);

  const safeMutateAsync = async (variables: TVariables, options?: Parameters<typeof mutationResult.mutateAsync>[1]) => {
    if (!listingAlias) {
      const error = new Error('We could not save your changes. Refresh the page and try again.') as TError;

      onError?.(error, variables, undefined as TContext);
      options?.onError?.(error, variables, undefined as TContext);
      throw error;
    }
    return mutationResult.mutateAsync(variables, options);
  };

  return {
    ...mutationResult,
    mutateAsync: safeMutateAsync,
  };
};
